import moment from 'moment/moment';
import { useDispatch, useSelector } from 'react-redux';

import { useUpdateAll } from './useUpdateAll';
import { useUpdateConnectionPositions } from './useUpdateConnectionPositions';
import { createWaterfall } from '../helpers/createWaterfall';
import { getChains } from '../helpers/getChains';
import { getConnectionsFromOtherItem } from '../helpers/getConnectionsFromOtherItem';
import { getCountDaysFromDate, getUpdatePositions } from '../helpers/getCountDays';
import { getLongPositions } from '../helpers/getLongPositions';
import { queueFetch } from '../helpers/queueFetch';
import { updateChain } from '../helpers/updateChain';
import { updatePositionWithoutConnect } from '../helpers/updatePositionWithoutConnect';
import { charterSelector } from '../store/reducers/charterSlice/charterSlice';
import { gantActions } from '../store/reducers/gantSlice/gantSlice';
import { removeManyConnections } from '../store/reducers/positionsConnections/actions/deleteManyConnections';
import { positionsConnectionsActions } from '../store/reducers/positionsConnections/positionsConnectionsSlice';
import { positionActions, positionSelector } from '../store/reducers/positionSlice/positionSlice';
import { actions } from '../store/reducers/stageSlice/stageSlice';

export const useEditStage = (stage) => {
    const dispatch = useDispatch();
    const allPositions = useSelector(positionSelector.selectEntities);
    const positionsEntities = useSelector(positionSelector.selectEntities);
    const allCharters = useSelector(charterSelector.selectEntities);

    const { updateConnectionPositions } = useUpdateConnectionPositions();

    const { settings } = useSelector((state) => state.gantReducer);

    const { updateAll, getStageChildren } = useUpdateAll(stage);

    const { allPositions: stagePositions, charters } = getStageChildren(positionsEntities);

    const stagePositionsGuid = stagePositions.map((posinion) => posinion.guid);
    const stageLabors = stagePositions.filter((position) => position.type === 'labor');
    const stageMHD = stagePositions.filter((position) => position.type !== 'labor');
    const lastLaborPosition = [...stagePositions]
        .filter((position) => position.type === 'labor')
        .sort(
            (positionA, positionB) =>
                moment(positionA.planned.end_date).valueOf() -
                moment(positionB.planned.end_date).valueOf()
        )
        .slice(-1)[0];

    const lastMHDPosition = [...stageMHD]
        .sort(
            (positionA, positionB) =>
                moment(positionA.planned.end_date).valueOf() -
                moment(positionB.planned.end_date).valueOf()
        )
        .slice(-1)[0];

    const lastPositionGuid = lastLaborPosition ? lastLaborPosition.guid : lastMHDPosition.guid;

    const positionsWithConnection = stagePositions
        .filter((position) => !!position.connections.length)
        .sort(
            (positionA, positionB) =>
                moment(positionA.planned.end_date).valueOf() -
                moment(positionB.planned.end_date).valueOf()
        );

    const getChainConnection = (position, allPosition) => {
        let result = [];

        const [nextPosition] = position.connections.filter(
            (connect) => connect.type === 0 && connect.to !== position.guid
        );

        if (!nextPosition) {
            return result;
        }

        result = [...result, allPosition[nextPosition.to]];
        const resultRec = getChainConnection(allPosition[nextPosition.to], allPosition);
        result = [...result, ...resultRec];
        return result;
    };
    const onHidePopup = () => dispatch(gantActions.changeShowPopup(false));

    const resetStage = (stage) => {
        const resetPlanned = {
            days_count: null,
            start_date: null,
            end_date: null,
        };
        const queue = [];
        const connections = [];

        const resetConnection = [];

        const updatedStage = { ...stage, planned: resetPlanned };
        const updatedCharters = [];

        stagePositions.forEach((currentPosition) => {
            currentPosition.connections.forEach((connection) => {
                const isRepeatedPosition = connections.some(
                    (connectionResult) =>
                        connectionResult.positionFromGuid === connection.from &&
                        connectionResult.positionToGuid === connection.to
                );
                if (!isRepeatedPosition) {
                    connections.push({
                        positionFromGuid: connection.from,
                        positionToGuid: connection.to,
                        connectionType: connection.type,
                    });
                }
            });
        });

        const resultPositionsEntities = { ...allPositions };

        stage.positions.forEach((stageChildGuid) => {
            if (allCharters[stageChildGuid]) {
                const updatedCharter = {
                    ...allCharters[stageChildGuid],
                    planned: resetPlanned,
                };

                updatedCharters.push({ id: stageChildGuid, changes: updatedCharter });

                allCharters[stageChildGuid].children.forEach((positionGuid) => {
                    const currentPosition = resultPositionsEntities[positionGuid];

                    if (currentPosition.connections.length) {
                        currentPosition.connections.forEach((connection) => {
                            if (connection.to === currentPosition.guid) {
                                const connectionsFrom = resultPositionsEntities[
                                    connection.from
                                ].connections.filter(
                                    (connectionFrom) => connectionFrom.to !== connection.to
                                );

                                resultPositionsEntities[connection.from] = {
                                    ...resultPositionsEntities[connection.from],
                                    connections: connectionsFrom,
                                };
                            } else {
                                const connectionsTo = resultPositionsEntities[
                                    connection.to
                                ].connections.filter(
                                    (connectionTo) => connectionTo.from !== connection.from
                                );

                                resultPositionsEntities[connection.to] = {
                                    ...resultPositionsEntities[connection.to],
                                    connections: connectionsTo,
                                };
                            }
                        });
                    }

                    const updatedPosition = {
                        ...resultPositionsEntities[positionGuid],
                        planned: resetPlanned,
                        connections: resetConnection,
                    };

                    queue.push({
                        data: {
                            data: {
                                guid: updatedPosition.guid,
                                startDate: null,
                                endDate: null,
                            },
                            type: 'update',
                            action: 'reset_stage',
                        },
                        subEntityType: 'step',
                        subEntityId: stage.id,
                        t: Date.now(),
                    });

                    resultPositionsEntities[positionGuid] = updatedPosition;
                });
            } else {
                const currentPosition = resultPositionsEntities[stageChildGuid];

                if (currentPosition.connections.length) {
                    currentPosition.connections.forEach((connection) => {
                        if (connection.to === currentPosition.guid) {
                            const connectionsFrom = resultPositionsEntities[
                                connection.from
                            ].connections.filter(
                                (connectionFrom) => connectionFrom.to !== connection.to
                            );

                            resultPositionsEntities[connection.from] = {
                                ...resultPositionsEntities[connection.from],
                                connections: connectionsFrom,
                            };
                        } else {
                            const connectionsTo = resultPositionsEntities[
                                connection.to
                            ].connections.filter(
                                (connectionTo) => connectionTo.from !== connection.from
                            );

                            resultPositionsEntities[connection.to] = {
                                ...resultPositionsEntities[connection.to],
                                connections: connectionsTo,
                            };
                        }
                    });
                }

                const updatedPosition = {
                    ...resultPositionsEntities[stageChildGuid],
                    planned: resetPlanned,
                    connections: resetConnection,
                };

                queue.push({
                    data: {
                        data: {
                            guid: updatedPosition.guid,
                            startDate: null,
                            endDate: null,
                        },
                        type: 'update',
                        action: 'reset_stage',
                    },
                    subEntityType: 'step',
                    subEntityId: stage.id,
                    t: Date.now(),
                });
                resultPositionsEntities[stageChildGuid] = updatedPosition;
            }
        });

        dispatch(actions.updateOne({ id: stage.id, changes: updatedStage }));

        const queueConnections = [
            {
                data: {
                    data: {
                        connection: connections,
                    },
                    type: 'remove_connections',
                    action: 'reset_stage',
                },
                subEntityType: 'step',
                subEntityId: stage.id,
                t: Date.now(),
            },
        ];

        const removedConnections = connections.map((connection) => ({
            from: connection.positionFromGuid,
            to: connection.positionToGuid,
            type: connection.connectionType,
        }));

        queueFetch(queueConnections);
        queueFetch(queue);
        updateAll(resultPositionsEntities);
        dispatch(removeManyConnections(removedConnections));
    };

    const createStage = (stage, stageForm) => {
        let planned;
        const queue = [];

        if (!stageLabors.length) {
            const updatedPositionsEntities = {};

            const start_date = stageForm.date
                ? stageForm.date
                : moment().hour(0).minute(0).seconds(0).format();

            const end_date = moment(start_date)
                .add(+stageForm.days - 1, 'days')
                .format();

            stageMHD.forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(start_date).format('YYYY-MM-DD'),
                            endDate: moment(end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'create_stage',
                    },
                    subEntityType: 'step',
                    subEntityId: stage.id,
                    t: Date.now(),
                });
                updatedPositionsEntities[position.guid] = {
                    ...position,
                    planned: {
                        start_date,
                        end_date,
                        days_count: stageForm.days,
                    },
                };
            });

            queueFetch(queue);

            updateAll(updatedPositionsEntities);
            return;
        }

        if (Number(stageForm.days) < stageLabors.length) {
            dispatch(gantActions.changeShowPopup(true));
        }

        const laborCount = stagePositions.filter((position) => position.type === 'labor').length;

        const resultDays = Number(stageForm.days) < laborCount ? `${laborCount}` : stageForm.days;

        const currentDate = stageForm.date
            ? stageForm.date
            : moment().hour(0).minute(0).seconds(0).format();

        planned = {
            days_count: `${Number(resultDays)}`,
            start_date: currentDate,
            end_date: moment()
                .hour(0)
                .minute(0)
                .seconds(0)
                .add(Number(resultDays) - 1, 'days')
                .format(),
        };

        const updatedStage = { ...stage, planned };

        const { long, remainder } = getLongPositions(laborCount, +resultDays);

        const mhdDaysCount =
            Number(updatedStage.planned.days_count) < +settings.daysMHD
                ? Number(updatedStage.planned.days_count)
                : settings.daysMHD;

        const plannedMhd = {
            start_date: currentDate,
            end_date: moment(currentDate)
                .add(+mhdDaysCount - 1, 'days')
                .format(),
            days_count: `${mhdDaysCount}`,
        };

        const { updatedEntities, connections } = createWaterfall(
            stagePositions,
            currentDate,
            positionsEntities,
            settings,
            lastPositionGuid,
            plannedMhd,
            remainder,
            long,
            charters
        );
        stagePositions
            .map((position) => updatedEntities[position.guid])
            .filter((position) => position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'create_stage',
                    },
                    subEntityType: 'step',
                    subEntityId: stage.id,
                    t: Date.now(),
                });
            });

        const queueConnections = [
            {
                data: {
                    data: {
                        connection: connections,
                    },
                    type: 'save_connections',
                    action: 'create_stage',
                },
                subEntityType: 'step',
                subEntityId: stage.id,
                t: Date.now(),
            },
        ];

        const newConnections = connections.map((connection) => ({
            from: connection.positionFromGuid,
            to: connection.positionToGuid,
            type: connection.connectionType,
        }));
        queueFetch(queue);
        queueFetch(queueConnections);

        updateAll(updatedEntities);
        dispatch(positionsConnectionsActions.addConnections(newConnections));
    };
    const increaseStage = (stage, charterForm, difference, pickerData = null) => {
        const updatedPositionsEntities = { ...allPositions };
        const queue = [];

        const endDate = moment(charterForm.date)
            .add(Number(charterForm.days) - 1, 'days')
            .format();

        const filteredLaborPosition = [...stagePositions]
            .filter((position) => position.planned.end_date)
            .filter((position) => position.type === 'labor')
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );

        const filteredMHDPosition = [...stagePositions]
            .filter((position) => position.planned.end_date)
            .filter((position) => position.type !== 'labor')
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );
        const lastPositionFiltered = filteredLaborPosition.length
            ? filteredLaborPosition.slice(-1)[0]
            : filteredMHDPosition.slice(-1)[0];

        const start = moment(lastPositionFiltered.planned.start_date);
        const end = moment(endDate);
        const duration = moment.duration(end.diff(start));
        const days = `${Math.round(+duration.asDays()) + 1}`;

        const planned = {
            ...lastPositionFiltered.planned,
            days_count: days,
            end_date: endDate,
        };

        const updatedPosition = { ...lastPositionFiltered, planned };

        queue.push({
            data: {
                data: {
                    guid: updatedPosition.guid,
                    startDate: moment(updatedPosition.planned.start_date).format('YYYY-MM-DD'),
                    endDate: moment(updatedPosition.planned.end_date).format('YYYY-MM-DD'),
                },
                type: 'update',
                action: 'increase_stage',
            },
            subEntityType: 'step',
            subEntityId: stage.id,
            t: Date.now(),
        });

        const moveDays = +charterForm.days - +stage.planned.days_count;

        const nextPositions = updatedPosition.connections
            .filter(
                (connection) => connection.type === 0 && updatedPosition.guid === connection.from
            )
            .map((connection) => allPositions[connection.to]);

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getChainConnection(position, allPositions),
        ]);

        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = allPositions[lastPositionFiltered.guid].planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    updatePosition.forEach((position) => {
                        queue.push({
                            data: {
                                data: {
                                    guid: position.changes.guid,
                                    startDate: moment(position.changes.planned.start_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                    endDate: moment(position.changes.planned.end_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                },
                                type: 'update',
                                action: 'increase_stage',
                            },
                            subEntityType: 'step',
                            subEntityId: stage.id,
                            t: Date.now(),
                        });
                    });

                    updatedPositionsEntities[updatedPosition.guid] = updatedPosition;

                    updatePosition.forEach((item) => {
                        updatedPositionsEntities[item.id] = item.changes;
                    });
                }
            });
        }

        const updatePositionEntity = {
            [updatedPosition.guid]: updatedPosition,
        };

        queueFetch(queue);
        updateAll({ ...updatedPositionsEntities, ...updatePositionEntity });
    };

    const decreaseStage = (stage, difference, pickerData = null) => {
        // let resultPositions = { ...allPositions };
        const queue = [];

        const positionWithConnection = stagePositions.filter(
            (position) => position.connections.length
        );

        const positionWithoutConnection = stagePositions.filter(
            (position) => !position.connections.length
        );

        let differenceRemainder = difference;

        const chains = getChains(positionWithConnection, lastPositionGuid, allPositions)
            .filter((chain) => chain.length > 1)
            .map((chain, index) => ({
                [index]: { chain, length: chain.length },
            }))
            .sort(
                (chainA, chainB) =>
                    Object.values(chainB)[0].length - Object.values(chainA)[0].length
            );

        const isConnection = !!chains.length;

        if (!isConnection) {
            const resultPositionNotConnection = {};

            stagePositions.forEach((position) => {
                resultPositionNotConnection[position.guid] = position;
            });

            let { planned } = stage;

            const days_count = +stage.planned.days_count - difference;

            if (days_count === 1) {
                planned = {
                    days_count: '1',
                    start_date: stage.planned.start_date,
                    end_date: stage.planned.start_date,
                };

                const positionsUpdatedData = stagePositions.map((position) => ({
                    id: position.guid,
                    changes: { ...position, planned },
                }));

                stagePositions
                    .filter((position) => position.planned.days_count)
                    .forEach((position) => {
                        resultPositionNotConnection[position.guid] = {
                            ...resultPositionNotConnection[position.guid],
                            planned,
                        };
                    });

                // dispatch(positionActions.updateMany(positionsUpdatedData));

                // TODO add updateAll
            } else {
                planned = {
                    ...planned,
                    days_count: `${days_count}`,
                    end_date: moment(planned.start_date)
                        .add(days_count - 1, 'days')
                        .format(),
                };

                stagePositions.forEach(({ guid }) => {
                    const currentPosition = resultPositionNotConnection[guid];

                    if (!currentPosition.planned.start_date) {
                        return;
                    }

                    let endDatePosition = currentPosition.planned.end_date;
                    let startDatePosition = currentPosition.planned.start_date;
                    const endDateCharter = planned.end_date;
                    const startDateCharter = planned.start_date;

                    if (moment(endDateCharter).isBefore(moment(endDatePosition))) {
                        const diff = moment(endDateCharter).diff(moment(endDatePosition), 'days');

                        endDatePosition = moment(currentPosition.planned.end_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        startDatePosition = moment(currentPosition.planned.start_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        let positionPlanned;

                        if (moment(startDatePosition).isBefore(moment(startDateCharter))) {
                            positionPlanned = {
                                ...planned,
                            };
                        } else {
                            positionPlanned = {
                                ...currentPosition.planned,
                                start_date: startDatePosition,
                                end_date: endDatePosition,
                            };
                        }

                        resultPositionNotConnection[guid] = {
                            ...currentPosition,
                            planned: positionPlanned,
                        };
                    }
                });

                const updateManyPositions = Object.values(resultPositionNotConnection).map(
                    (position) => ({
                        id: position.guid,
                        changes: position,
                    })
                );
                // TODO use updateAll

                dispatch(positionActions.updateMany(updateManyPositions));
            }

            Object.values(resultPositionNotConnection)
                .filter((position) => position.planned.start_date)
                .forEach((position) => {
                    queue.push({
                        data: {
                            data: {
                                guid: position.guid,
                                startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                                endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                            },
                            type: 'update',
                            action: 'decrease_stage',
                        },
                        subEntityType: 'step',
                        subEntityId: stage.id,
                        t: Date.now(),
                    });
                });

            updateAll(resultPositionNotConnection);

            queueFetch(queue);

            return;
        }

        const longestChain = Object.values(chains[0])[0];

        if (+stage.planned.days_count - difference < longestChain.length) {
            dispatch(gantActions.changeShowPopup(true));
        }

        differenceRemainder =
            +stage.planned.days_count - difference < longestChain.length
                ? difference - (longestChain.length - (+stage.planned.days_count - difference))
                : difference;

        // let endDate = moment(stage.planned.end_date).add(-difference, 'days').format();
        const daysCount = +stage.planned.days_count - differenceRemainder;

        let resultPositions = {};

        [...positionWithConnection, ...positionWithoutConnection].forEach((position) => {
            resultPositions[position.guid] = position;
        });

        chains.forEach((chain) => {
            const filteredChain = Object.values(chain)[0].chain.filter((position, index) => {
                if (index === 0 && !resultPositions[position.guid]) {
                    return false;
                }
                return true;
            });
            resultPositions = {
                ...resultPositions,
                ...updateChain(
                    filteredChain,
                    resultPositions,
                    stage.planned,
                    differenceRemainder,
                    allPositions,
                    updateConnectionPositions
                ),
            };
        });

        positionWithoutConnection.forEach((position) => {
            resultPositions = {
                ...resultPositions,
                ...updatePositionWithoutConnect(
                    position,
                    resultPositions,
                    stage.planned,
                    differenceRemainder,
                    allPositions
                ),
            };
        });

        stagePositions
            .map((position) => resultPositions[position.guid])
            .filter((position) => position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'decrease_stage',
                    },
                    subEntityType: 'step',
                    subEntityId: stage.id,
                    t: Date.now(),
                });
            });

        queueFetch(queue);
        updateAll(resultPositions);
    };

    const increaseStageNew = (charterForm, difference, positionsEntities) => {
        const updatedPositionsEntities = { ...positionsEntities };

        const positions = { ...allPositions, ...updatedPositionsEntities };

        const { allPositions: stagePositions } = getStageChildren(positions);

        const endDate = moment(charterForm.date)
            .add(Number(charterForm.days) - 1, 'days')
            .format();

        // const filteredPosition = [...stagePositions]
        //     .filter((position) => position.planned.end_date)
        //     .sort(
        //         (positionA, positionB) =>
        //             moment(positionA.planned.end_date).valueOf() -
        //             moment(positionB.planned.end_date).valueOf()
        //     );
        //
        // const lastPositionFiltered = filteredPosition.slice(-1)[0];

        const filteredLaborPosition = [...stagePositions]
            .filter((position) => position.planned.end_date)
            .filter((position) => position.type === 'labor')
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );

        const filteredMHDPosition = [...stagePositions]
            .filter((position) => position.planned.end_date)
            .filter((position) => position.type !== 'labor')
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );
        const lastPositionFiltered = filteredLaborPosition.length
            ? filteredLaborPosition.slice(-1)[0]
            : filteredMHDPosition.slice(-1)[0];

        const start = moment(lastPositionFiltered.planned.start_date);
        const end = moment(endDate);
        const duration = moment.duration(end.diff(start));
        const days = `${Math.round(+duration.asDays()) + 1}`;

        const planned = {
            ...lastPositionFiltered.planned,
            days_count: days,
            end_date: endDate,
        };

        const updatedPosition = { ...lastPositionFiltered, planned };

        const moveDays = difference;

        const nextPositions = updatedPosition.connections
            .filter(
                (connection) => connection.type === 0 && updatedPosition.guid === connection.from
            )
            .map((connection) => positions[connection.to]);

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getChainConnection(position, positions),
        ]);

        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = positions[lastPositionFiltered.guid].planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    updatedPositionsEntities[updatedPosition.guid] = updatedPosition;

                    updatePosition.forEach((item) => {
                        updatedPositionsEntities[item.id] = item.changes;
                    });
                }
            });
        }

        const updatePositionEntity = {
            [updatedPosition.guid]: updatedPosition,
        };

        return { ...updatedPositionsEntities, ...updatePositionEntity };
    };

    const moveStageNew = (moveDays, positionsEntities, isNeedQueueFetch = true) => {
        const updatedPositionsEntities = { ...positionsEntities };
        const queue = [];

        if (!positionsWithConnection.length) {
            stagePositions.forEach((position) => {
                const planned = {
                    ...position.planned,
                    start_date: moment(position.planned.start_date).add(moveDays, 'days').format(),
                    end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
                };

                if (position.planned.start_date) {
                    updatedPositionsEntities[position.guid] = { ...position, planned };

                    queue.push({
                        data: {
                            data: {
                                guid: position.guid,
                                startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                                endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                            },
                            type: 'update',
                            action: 'move_stage',
                        },
                        subEntityType: 'step',
                        subEntityId: stage.id,
                        t: Date.now(),
                    });
                }
            });

            if (isNeedQueueFetch) {
                queueFetch(queue);
            }
            return updatedPositionsEntities;
        }

        const chainsPositionFromOtherItem = getConnectionsFromOtherItem(
            stagePositions,
            allPositions
        );

        if (moveDays > 0) {
            const firstPositionWithPrev = stagePositions
                .sort(
                    (positionA, positionB) =>
                        moment(positionB.planned.end_date).valueOf() -
                        moment(positionA.planned.end_date).valueOf()
                )
                .find((position) => {
                    let result = false;
                    position.connections.forEach((connection) => {
                        if (
                            connection.type === 1 &&
                            connection.to === position.guid &&
                            !stagePositionsGuid.includes(connection.from)
                        ) {
                            result = true;
                        }
                    });
                    return result;
                });

            const prevPositionConnection = firstPositionWithPrev?.connections.find(
                (connection) =>
                    connection.to === firstPositionWithPrev?.guid &&
                    connection.type === 1 &&
                    !stagePositionsGuid.includes(connection.from)
            );

            if (!prevPositionConnection) {
                stagePositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(moveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        stagePositionsGuid.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, moveDays);

                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_stage',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: stage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            } else {
                                const initEndDate =
                                    allPositions[connectionWithCurrentPosition.from].planned
                                        .end_date;

                                const nextPositionStartDate = firstChainPosition.planned.start_date;

                                const diffBeforePosition =
                                    moment(nextPositionStartDate).diff(
                                        moment(initEndDate),
                                        'days'
                                    ) - 1;

                                if (diffBeforePosition - moveDays < 0) {
                                    const diff = Math.abs(diffBeforePosition - moveDays);

                                    const updatePosition = getUpdatePositions(chain, diff);

                                    updatePosition.forEach((position) => {
                                        queue.push({
                                            data: {
                                                data: {
                                                    guid: position.changes.guid,
                                                    startDate: moment(
                                                        position.changes.planned.start_date
                                                    ).format('YYYY-MM-DD'),
                                                    endDate: moment(
                                                        position.changes.planned.end_date
                                                    ).format('YYYY-MM-DD'),
                                                },
                                                type: 'update',
                                                action: 'move_stage',
                                            },
                                            subEntityType: 'step',
                                            subEntityId: stage.id,
                                            t: Date.now(),
                                        });
                                    });

                                    updatePosition.forEach((item) => {
                                        updatedPositionsEntities[item.id] = item.changes;
                                    });
                                }
                            }
                        });
                    });
                }
            } else {
                const prevPosition = allPositions[prevPositionConnection.from];

                const diffBeforeConnectPosition =
                    moment(prevPosition.planned.start_date).diff(
                        moment(firstPositionWithPrev.planned.end_date),
                        'days'
                    ) - 1;

                const newMoveDays =
                    diffBeforeConnectPosition - moveDays < 0 ? diffBeforeConnectPosition : moveDays;

                stagePositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(newMoveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date)
                            .add(newMoveDays, 'days')
                            .format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        stagePositionsGuid.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, newMoveDays);

                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_stage',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: stage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            } else {
                                const initEndDate =
                                    allPositions[connectionWithCurrentPosition.from].planned
                                        .end_date;

                                const nextPositionStartDate = firstChainPosition.planned.start_date;

                                const diffBeforePosition =
                                    moment(nextPositionStartDate).diff(
                                        moment(initEndDate),
                                        'days'
                                    ) - 1;

                                if (diffBeforePosition - newMoveDays < 0) {
                                    const diff = Math.abs(diffBeforePosition - newMoveDays);

                                    const updatePosition = getUpdatePositions(chain, diff);

                                    updatePosition.forEach((position) => {
                                        queue.push({
                                            data: {
                                                data: {
                                                    guid: position.changes.guid,
                                                    startDate: moment(
                                                        position.changes.planned.start_date
                                                    ).format('YYYY-MM-DD'),
                                                    endDate: moment(
                                                        position.changes.planned.end_date
                                                    ).format('YYYY-MM-DD'),
                                                },
                                                type: 'update',
                                                action: 'move_stage',
                                            },
                                            subEntityType: 'step',
                                            subEntityId: stage.id,
                                            t: Date.now(),
                                        });
                                    });

                                    updatePosition.forEach((item) => {
                                        updatedPositionsEntities[item.id] = item.changes;
                                    });
                                }
                            }
                        });
                    });
                }
            }
        } else {
            const firstPositionWithPrev = stagePositions
                .sort(
                    (positionA, positionB) =>
                        moment(positionA.planned.start_date).valueOf() -
                        moment(positionB.planned.start_date).valueOf()
                )
                .find((position) => {
                    let result = false;
                    position.connections.forEach((connection) => {
                        if (
                            connection.type === 0 &&
                            connection.to === position.guid &&
                            !stagePositionsGuid.includes(connection.from)
                        ) {
                            result = true;
                        }
                    });
                    return result;
                });

            const prevPositionConnection = firstPositionWithPrev?.connections.find(
                (connection) =>
                    connection.to === firstPositionWithPrev?.guid &&
                    connection.type === 0 &&
                    !stagePositionsGuid.includes(connection.from)
            );

            if (!prevPositionConnection) {
                stagePositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(moveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        stagePositionsGuid.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, moveDays);

                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_stage',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: stage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            }
                        });
                    });
                }
            } else {
                const prevPosition = allPositions[prevPositionConnection.from];
                const diffBeforeConnectPosition =
                    moment(firstPositionWithPrev.planned.start_date).diff(
                        moment(prevPosition.planned.end_date),
                        'days'
                    ) - 1;

                const newMoveDays =
                    diffBeforeConnectPosition + moveDays < 0
                        ? -diffBeforeConnectPosition
                        : moveDays;

                stagePositions.forEach((position) => {
                    const planned = {
                        ...position.planned,
                        start_date: moment(position.planned.start_date)
                            .add(newMoveDays, 'days')
                            .format(),
                        end_date: moment(position.planned.end_date)
                            .add(newMoveDays, 'days')
                            .format(),
                    };
                    if (position.planned.start_date) {
                        updatedPositionsEntities[position.guid] = { ...position, planned };
                    }
                });

                if (chainsPositionFromOtherItem.length) {
                    chainsPositionFromOtherItem.forEach((chainsConnections) => {
                        chainsConnections.forEach((chain) => {
                            const firstChainPosition = chain[0];
                            const connectionWithCurrentPosition =
                                firstChainPosition.connections.filter(
                                    (connection) =>
                                        connection.to === firstChainPosition.guid &&
                                        stagePositionsGuid.includes(connection.from)
                                )[0];

                            if (connectionWithCurrentPosition.type === 1) {
                                const updatePosition = getUpdatePositions(chain, newMoveDays);

                                updatePosition.forEach((position) => {
                                    queue.push({
                                        data: {
                                            data: {
                                                guid: position.changes.guid,
                                                startDate: moment(
                                                    position.changes.planned.start_date
                                                ).format('YYYY-MM-DD'),
                                                endDate: moment(
                                                    position.changes.planned.end_date
                                                ).format('YYYY-MM-DD'),
                                            },
                                            type: 'update',
                                            action: 'move_stage',
                                        },
                                        subEntityType: 'step',
                                        subEntityId: stage.id,
                                        t: Date.now(),
                                    });
                                });
                                updatePosition.forEach((item) => {
                                    updatedPositionsEntities[item.id] = item.changes;
                                });
                            }
                        });
                    });
                }
            }
        }

        stagePositions
            .map((position) => updatedPositionsEntities[position.guid])
            .filter((position) => position && position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'move_stage',
                    },
                    subEntityType: 'step',
                    subEntityId: stage.id,
                    t: Date.now(),
                });
            });

        if (isNeedQueueFetch) {
            queueFetch(queue);
        }

        return updatedPositionsEntities;
    };

    const decreaseStageNew = (difference, positionsEntities, pickerData = null) => {
        const updatedPositionsEntities = { ...positionsEntities };
        const positions = { ...allPositions, ...updatedPositionsEntities };

        const { allPositions: stagePositions } = getStageChildren(positions);
        const positionWithConnection = stagePositions.filter(
            (position) => position.connections.length
        );

        const positionWithoutConnection = stagePositions.filter(
            (position) => !position.connections.length
        );

        let differenceRemainder = Math.abs(difference);

        const isConnection = !!positionWithConnection.length;

        if (!isConnection) {
            const resultPositions = { ...updatedPositionsEntities };

            stagePositions.forEach((position) => {
                resultPositions[position.guid] = position;
            });

            let { planned } = stage;
            const stageStartDate = pickerData
                ? moment(pickerData.start).format()
                : stage.planned.start_date;
            const days_count = pickerData
                ? +pickerData.days
                : +stage.planned.days_count - Math.abs(difference);

            if (days_count === 1) {
                planned = {
                    days_count: '1',
                    start_date: stageStartDate,
                    end_date: stageStartDate,
                };

                stagePositions
                    .filter((position) => position.planned.days_count)
                    .forEach((position) => {
                        resultPositions[position.guid] = {
                            ...resultPositions[position.guid],
                            planned,
                        };
                    });
            } else {
                planned = {
                    ...planned,
                    days_count: `${days_count}`,
                    start_date: stageStartDate,
                    end_date: moment(stageStartDate)
                        .add(days_count - 1, 'days')
                        .format(),
                };

                stagePositions.forEach(({ guid }) => {
                    const currentPosition = resultPositions[guid];

                    if (!currentPosition?.planned?.start_date) {
                        return;
                    }

                    let endDatePosition = currentPosition.planned.end_date;
                    let startDatePosition = currentPosition.planned.start_date;
                    const endDateCharter = planned.end_date;
                    const startDateCharter = planned.start_date;

                    if (moment(endDateCharter).isBefore(moment(endDatePosition))) {
                        const diff = moment(endDateCharter).diff(moment(endDatePosition), 'days');

                        endDatePosition = moment(currentPosition.planned.end_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        startDatePosition = moment(currentPosition.planned.start_date)
                            .add(-Math.abs(diff), 'days')
                            .format();

                        let positionPlanned;

                        if (moment(startDatePosition).isBefore(moment(startDateCharter))) {
                            positionPlanned = {
                                ...planned,
                            };
                        } else {
                            positionPlanned = {
                                ...currentPosition.planned,
                                start_date: startDatePosition,
                                end_date: endDatePosition,
                            };
                        }

                        resultPositions[guid] = {
                            ...currentPosition,
                            planned: positionPlanned,
                        };
                    }
                });

                const updateManyPositions = Object.values(resultPositions).map(
                    (position) => ({
                        id: position.guid,
                        changes: position,
                    })
                );
                // TODO use updateAll

                // dispatch(positionActions.updateMany(updateManyPositions));
            }

            // updatePlannedParents(planned, 'charter');
            // updateAll(resultPositionNotConnection);

            return resultPositions;
        }

        const chains = getChains(positionWithConnection, lastPositionGuid, positions)
            .filter((chain) => chain.length > 1)
            .map((chain, index) => ({
                [index]: { chain, length: chain.length },
            }))
            .sort(
                (chainA, chainB) =>
                    Object.values(chainB)[0].length - Object.values(chainA)[0].length
            );

        const longestChain = Object.values(chains[0])[0];

        if (+stage.planned.days_count - Math.abs(difference) < longestChain.length) {
            dispatch(gantActions.changeShowPopup(true));
        }

        differenceRemainder =
            +stage.planned.days_count - Math.abs(difference) < longestChain.length
                ? Math.abs(difference) -
                  (longestChain.length - (+stage.planned.days_count - Math.abs(difference)))
                : Math.abs(difference);

        // let endDate = moment(stage.planned.end_date).add(-difference, 'days').format();
        const daysCount = +stage.planned.days_count - differenceRemainder;

        let resultPositions = { ...updatedPositionsEntities };

        [...positionWithConnection, ...positionWithoutConnection].forEach((position) => {
            resultPositions[position.guid] = position;
        });

        const sortedPositions = stagePositions
            .filter((position) => position.planned.start_date)
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );

        const { start_date } = sortedPositions[0].planned;
        const { end_date } = sortedPositions.slice(-1)[0].planned;
        const days_count = getCountDaysFromDate(start_date, end_date);
        const updatedPlanned = {
            start_date,
            end_date,
            days_count: String(days_count),
        };

        chains.forEach((chain) => {
            const filteredChain = Object.values(chain)[0].chain.filter((position, index) => {
                if (index === 0 && !resultPositions[position.guid]) {
                    return false;
                }
                return true;
            });
            resultPositions = {
                ...resultPositions,
                ...updateChain(
                    filteredChain,
                    resultPositions,
                    updatedPlanned,
                    differenceRemainder,
                    positions,
                    updateConnectionPositions
                ),
            };
        });

        positionWithoutConnection.forEach((position) => {
            resultPositions = {
                ...resultPositions,
                ...updatePositionWithoutConnect(
                    position,
                    resultPositions,
                    updatedPlanned,
                    differenceRemainder,
                    positions
                ),
            };
        });

        return { ...updatedPositionsEntities, ...resultPositions };

        // updateAll(resultPositions);
    };

    const onDataPickerStage = (stage, pickerData) => {
        const stageInitStartDate = stage.planned.start_date;
        const isNeedMove = pickerData.start !== stage.planned.start_date;
        const isNeedResize = pickerData.days !== stage.planned.days_count;

        const moveDays = moment(pickerData.start).diff(moment(stageInitStartDate), 'days');

        let resultUpdating = {};

        if (isNeedMove) {
            const moveResult = moveStageNew(moveDays, resultUpdating, false);
            resultUpdating = { ...resultUpdating, ...moveResult };
        }
        if (isNeedResize) {
            const isDecrease = +stage.planned.days_count > +pickerData.days;
            const diff = +pickerData.days - +stage.planned.days_count;

            if (isDecrease) {
                const decreaseResult = decreaseStageNew(diff, resultUpdating, pickerData);
                resultUpdating = { ...resultUpdating, ...decreaseResult };
            } else {
                const increaseResult = increaseStageNew(
                    { date: pickerData.start, days: pickerData.days },
                    diff,
                    resultUpdating
                );
                resultUpdating = { ...resultUpdating, ...increaseResult };
            }
        }

        const queue = [];
        Object.values(resultUpdating)
            .filter((position) => position.planned.start_date)
            .forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'picker_update_stage',
                    },
                    subEntityType: 'step',
                    subEntityId: stage.id,
                    t: Date.now(),
                });
            });

        queueFetch(queue);
        updateAll(resultUpdating);
    };

    return {
        resetStage,
        createStage,
        increaseStage,
        decreaseStage,
        onHidePopup,
        moveStageNew,
        onDataPickerStage,
        updateAll,
    };
};
